import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, Tabs } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { getPayableById, getReceivableById } from '@/api/finance';
import { useAuth } from '@/contexts/AuthContext';
import { Payable } from '@/types/finance/payable';
import { Receivable } from '@/types/finance/receivable';
import { Receipt } from '@/types';
import dayjs from 'dayjs';
import { formatFirestoreTimestamp } from '@/utils/date';

const { Title } = Typography;

const AccountingDetailsPage: React.FC = () => {
  const { t } = useTranslation(['accounting', 'common', 'payables', 'receivables']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { firestoreUser } = useAuth();
  const state = location.state as { record: Payable | Receivable; type: 'payable' | 'receivable' } | null;
  
  // Queries
  const { data: payable, isLoading: isLoadingPayable } = useQuery({
    queryKey: ['payable', id],
    queryFn: () => getPayableById(id!, firestoreUser!),
    enabled: !!id && !!firestoreUser && !state?.record && state?.type === 'payable',
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: receivable, isLoading: isLoadingReceivable } = useQuery({
    queryKey: ['receivable', id],
    queryFn: () => getReceivableById(id!, firestoreUser!),
    enabled: !!id && !!firestoreUser && !state?.record && state?.type === 'receivable',
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Use state data if available, otherwise use fetched data
  const displayRecord = state?.record || payable || receivable;
  const recordType = state?.type || (payable ? 'payable' : 'receivable');
  const isLoading = (isLoadingPayable || isLoadingReceivable) && !state?.record;

  // Loading state - only show if no state data is available
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayRecord) {
    return <Card>Record not found</Card>;
  }

  const formatMoney = (amount: any) => {
    if (!amount || typeof amount !== 'object') return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: amount.currency || 'USD'
    }).format(amount.amount);
  };

  const getStatusTagColor = (status: string) => {
    switch (status) {
      case 'paid': return 'green';
      case 'pending': return 'orange';
      case 'partial': return 'blue';
      case 'overdue': return 'red';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getTypeTagColor = (type: string) => {
    return type === 'payable' ? 'red' : 'green';
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Get completed receipts
  const completedReceipts = displayRecord.receipts?.filter((r: Receipt) => r.status === 'completed') || [];

  const items = [
    {
      key: 'receipts',
      label: t('common:receipts'),
      children: (
        <div>
          {completedReceipts.length > 0 ? (
            <div>
              {completedReceipts.map((receipt: Receipt, index: number) => (
                <Card key={receipt.id || index} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions bordered column={2}>
                    <Descriptions.Item label={t('common:name')}>
                      {receipt.name}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('common:statusLabel')}>
                      <Tag color="green">{t('common:completed')}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('common:amount')}>
                      <span style={{ fontWeight: 500, color: '#52c41a' }}>
                        {formatMoney(receipt.amount)}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('common:receiptDate')}>
                      {receipt.date ? dayjs(formatFirestoreTimestamp(receipt.date)).format('MMM DD, YYYY') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('common:description')} span={2}>
                      {receipt.description}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              {t('common:noReceipts')}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'details',
      label: t('common:details'),
      children: (
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('common:name')}>
            {displayRecord.name}
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:typeLabel')}>
            <Tag color={getTypeTagColor(recordType)}>
              {t(`accounting:types.${recordType}`)}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:amount')}>
            <span style={{ fontWeight: 500, color: '#52c41a' }}>
              {formatMoney(displayRecord.amount)}
            </span>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:dueDate')}>
            {displayRecord.dueDate ? dayjs(formatFirestoreTimestamp(displayRecord.dueDate)).format('MMM DD, YYYY') : '-'}
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:statusLabel')}>
            <Tag color={getStatusTagColor(displayRecord.status)}>
              {recordType === 'payable' 
                ? t(`payables:statuses.${displayRecord.status}`)
                : t(`receivables:statuses.${displayRecord.status}`)
              }
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:description')}>
            {displayRecord.description}
          </Descriptions.Item>
          
          {displayRecord.notes && (
            <Descriptions.Item label={t('common:notes')}>
              {displayRecord.notes}
            </Descriptions.Item>
          )}

          {/* Show reference information based on type */}
          {recordType === 'payable' && (displayRecord as Payable).vendor && (
            <Descriptions.Item label={t('common:vendor')}>
              {(displayRecord as Payable).vendor?.name}
            </Descriptions.Item>
          )}
          
          {recordType === 'payable' && (displayRecord as Payable).employee && (
            <Descriptions.Item label={t('common:employee')}>
              {(displayRecord as Payable).employee?.name}
            </Descriptions.Item>
          )}
          
          {recordType === 'payable' && (displayRecord as Payable).asset && (
            <Descriptions.Item label={t('common:asset')}>
              {(displayRecord as Payable).asset?.name}
            </Descriptions.Item>
          )}
          
          {recordType === 'receivable' && (displayRecord as Receivable).resident && (
            <Descriptions.Item label={t('common:resident')}>
              {(displayRecord as Receivable).resident?.name}
            </Descriptions.Item>
          )}
          
          {recordType === 'receivable' && (displayRecord as Receivable).employee && (
            <Descriptions.Item label={t('common:employee')}>
              {(displayRecord as Receivable).employee?.name}
            </Descriptions.Item>
          )}
          
          {recordType === 'receivable' && (displayRecord as Receivable).vendor && (
            <Descriptions.Item label={t('common:vendor')}>
              {(displayRecord as Receivable).vendor?.name}
            </Descriptions.Item>
          )}
        </Descriptions>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              {t('common:back')}
            </Button>
            <Title level={3}>{displayRecord.name}</Title>
          </Space>
        </Col>
      </Row>
      
      {/* Content */}
      <Card>
        <Tabs items={items} />
      </Card>
    </div>
  );
};

export default AccountingDetailsPage; 